import React, { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, X } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useCompare } from '../hooks/useCompare';

const CompareBar = () => {
    const { compareIds, removeCompare, clearCompare, trekNames } = useCompare();
    const { pathname } = useLocation();
    const prefersReducedMotion = useReducedMotion();
    const inCompareFlow =
        sessionStorage.getItem('ftr_show_compare') === '1' ||
        pathname.startsWith('/compare') ||
        pathname === '/treks';
    if (!compareIds || compareIds.length < 2 || !inCompareFlow) return null;

    const Wrapper = prefersReducedMotion ? 'div' : motion.div;
    const wrapperProps = prefersReducedMotion ? {} : {
        initial: { y: 100, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: 100, opacity: 0 },
        transition: { type: 'spring', damping: 25, stiffness: 300 },
    };

    return (
        <AnimatePresence>
            <Wrapper
                className="fixed bottom-[72px] lg:bottom-0 left-0 right-0 z-[1050] border-t border-gray-200 bg-white/95 backdrop-blur-xl shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
                {...wrapperProps}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
                    <div className="flex items-center gap-2 min-w-0">
                        <BarChart3 size={16} className="shrink-0 text-emerald-600" />
                        <span
                            className="text-sm font-semibold text-gray-700 whitespace-nowrap">{compareIds.length} treks selected</span>
                        <button onClick={clearCompare}
                                className="shrink-0 text-xs font-medium text-gray-400 hover:text-red-500 transition-colors">Clear
                        </button>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div
                            className="hidden sm:flex items-center gap-1.5 overflow-x-auto scrollbar-hide max-w-[200px]">
                            {compareIds.map(id => (
                                <span key={id}
                                      className="inline-flex items-center gap-1 rounded-[20px] bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px] font-semibold text-emerald-700 shrink-0">
                                {trekNames[id] || `#${id}`}
                                    <button onClick={() => removeCompare(id)}
                                            className="flex items-center justify-center p-0.5 text-emerald-400 hover:text-red-500 transition-colors"><X
                                        size={11} /></button>
                            </span>
                            ))}
                        </div>
                        <Link to={`/compare?ids=${compareIds.join(',')}`}
                              className="rounded-[20px] bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.97] shrink-0">
                            Compare
                        </Link>
                    </div>
                </div>
            </Wrapper>
        </AnimatePresence>
    );
};

export default memo(CompareBar);
